import type { overview as en } from '@/i18n/locales/en/overview';
import type { Translation } from '@/i18n/types';

export const overview: Translation<typeof en> = {
  page: {
    title: 'Resumen',
    lead: '¿Cómo le va a {{company}}?',
    noScope: 'Selecciona una empresa para ver su resumen.',
    noDepartments: 'Ningún departamento aquí tiene estadísticas que puedas ver.',
    disclaimer:
      'Todos los valores son indicaciones de bienestar a nivel de grupo y no están destinados a ser un diagnóstico médico.',
  },
  summary: {
    caption: 'Resumen de IA basado en datos anonimizados y agregados.',
  },
  figures: {
    vitality: 'Vitalidad',
    stress: 'Estrés',
    sleep: 'Sueño',
    engagement: 'Participación',
    activeParticipants: 'Participantes activos',
  },
  figuresInfo: {
    vitality:
      'Una puntuación compuesta basada en sueño, actividad, HRV, estrés y frecuencia cardíaca.',
    stress: 'Nivel medio de estrés, medido directamente por el wearable.',
    sleep: 'Duración media del sueño por noche, medida directamente por el wearable.',
    engagement: 'Proporción de empleados que usan activamente la aplicación.',
    activeParticipants: 'Número de empleados activos durante el último período.',
  },
  legend: {
    title: 'Lo que ves',
    measured:
      'Medido directamente — directamente desde el wearable NOLATR o un sensor conectado. Máxima fiabilidad.',
    aiDerived:
      'Derivado por IA — no es una medición directa. La IA combina varias señales en un indicador.',
    usage: 'Uso y comportamiento — interacciones reales en la app, no una medición de salud.',
  },
  developments: {
    title: 'Principales novedades',
    empty: 'No hay novedades destacadas este período.',
    stress: {
      title: 'Oportunidad de ofrecer descanso (estrés elevado)',
      body: 'Un momento de descanso amable o un consejo de respiración puede ayudar al equipo a recargar energías.',
    },
    sleep: {
      title: 'Oportunidad: sueño por debajo del objetivo',
      body: 'Un consejo de recuperación específico y positivo puede apoyar bien al equipo.',
    },
    vitalityGood: {
      title: 'La vitalidad evoluciona bien',
      body: 'El equipo mantiene bien su ritmo actual — un buen momento para reconocerlo.',
    },
    needsReactivation: {
      title: '{{count}} personas necesitan reactivación',
      body: 'Un mensaje de reactivación breve y personal suele funcionar mejor.',
    },
    mostActiveWeekday: {
      title: 'Más energía el {{weekday}}',
      body: 'Planifica retos y comunicaciones en torno al día más activo para lograr el máximo alcance.',
    },
    challengeParticipation: {
      title: 'Participación en el reto {{pct}}%',
      body: 'Un nuevo reto de equipo accesible podría animar a más personas a participar.',
    },
  },
  actions: {
    title: 'Acciones para RR. HH.',
    empty: 'No hay acciones recomendadas por ahora.',
    sendReminder: {
      title: '{{count}} empleados aún no activados',
      subtitle: 'Envía un recordatorio',
    },
    reactivate: {
      title: '{{count}} empleados necesitan reactivación',
      subtitle: 'Contacta con ellos',
    },
    stressCheckIn: {
      title: 'Estrés elevado en el equipo',
      subtitle: 'Considera una breve conversación sobre la carga de trabajo',
    },
    sleepReminder: {
      title: 'Sueño por debajo del objetivo en el equipo',
      subtitle: 'Considera un recordatorio sobre las rutinas nocturnas y el tiempo de pantalla',
    },
    activeChallenge: {
      title: "'{{name}}' está en marcha — planifica el siguiente",
      subtitle: 'Retos',
    },
    pilotEvaluation: {
      interim: {
        title: "El piloto '{{name}}' está listo para su evaluación intermedia",
        subtitle: 'Ver piloto',
      },
      final: {
        title: "El piloto '{{name}}' está listo para su evaluación final",
        subtitle: 'Ver piloto',
      },
    },
  },
  energy: {
    title: 'Energía y bienestar laboral',
    subtitle:
      'Cómo vive el equipo la jornada laboral — energía, pausas, ritmo de trabajo y participación. Solo agregado, nunca individual.',
    badge: 'Agregado y anonimizado',
    tbd: 'Por determinar',
    missingData: {
      noSource: 'Índice de energía y eNPS: todavía sin fuente de datos determinada.',
      insufficientParticipants: 'Participantes insuficientes para {{metrics}}.',
    },
    privacyNote:
      'Información de apoyo, no vigilancia. Bienestar indicativo a nivel de grupo — sin interpretación médica.',
    metrics: {
      energyIndex: 'Índice de energía',
      breakAdoption: 'Adopción de pausas',
      focusRhythm: 'Ritmo de concentración',
      coachAdoption: 'Adopción del coach',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Estado de los pilotos',
    empty: 'Todavía no hay pilotos.',
    participantsAndDay: '{{count}} participantes · día {{day}} de {{total}}',
    viewReport: 'Ver informe del piloto',
  },
  challenges: {
    title: 'Retos activos',
    empty: 'Todavía no hay retos.',
    active: 'Activos',
    total: 'Total',
    participants: 'Participantes',
    activeBadge: '{{count}} activos',
    manageLink: 'Gestionar retos',
    noneActive: 'No hay retos activos en este momento.',
    participantsCount: '{{count}} participantes',
  },
  trends: {
    title: 'Tendencias',
    filters: {
      allDepartments: 'Todos los departamentos',
      participants: {
        label: 'Participantes',
        all: 'Todos los participantes',
        manager: 'Directivos',
        employee: 'Empleados',
      },
    },
    vitalityScore: 'Puntuación de vitalidad en el tiempo',
    activeUsersPerWeek: 'Usuarios activos por semana',
    sleep: 'Tendencia de sueño',
    stress: 'Tendencia de estrés',
    hrv: 'Tendencia de VFC',
    recovery: 'Tendencia de recuperación',
    activity: 'Tendencia de actividad',
    notAvailable:
      'Todavía no hay fuente de datos para esta tendencia — no disponible en esta versión.',
    vsPreviousPeriod: 'frente al periodo anterior',
    info: {
      vitalityScore:
        'Una puntuación de vitalidad compuesta por día, basada en todas las señales de vitalidad.',
      activeUsersPerWeek:
        'Número de empleados únicos que usaron activamente la aplicación cada semana.',
      sleep: 'Duración media del sueño por día, medida directamente por el wearable.',
      stress: 'Nivel medio de estrés por día, medido directamente por el wearable.',
      hrv: 'Variabilidad media de la frecuencia cardíaca (HRV) por día, un indicador de recuperación.',
      recovery:
        'Una puntuación de recuperación compuesta por día, basada en sueño, HRV y actividad.',
      activity: 'Número medio de pasos por día, medido directamente por el wearable.',
    },
  },
};
